import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
  signOut,
} from "firebase/auth";
import { auth } from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { serverTimestamp } from "firebase/firestore";
import { assignUserRole } from "../../utils/roleManagement";
import { setUser } from "../../../redux/actions";
import { clearRegistration } from "../../../redux/actionsFolder/registrationActions";
import tpcLogo from "../../../images/TPC-logo-light.png";
import tpcAndUSLogo from "../../../images/AndUsLoginLogo.png";
import LoadingCircleSmall from "../../utils/LoadingCircleSmall";
import TermsModal from "./TermsModal";
import CustomDropdown from "./CustomDropdown";
import EmailValidationModal from "./EmailValidationModal";
import "./HeroRegisterManual.css";

/*────────── CONFIG──────────*/
const CONFIG = {
  company: "TPC_Group",
  collectionBrand: "TechPerformanceCentre",
  systemName: "Users",
  role: "Hero-TPC_Group-TechPerformanceCentre",
  state: "Active",
  successRoute: "/hero-dashboard",
  loginRoute: "/hero-login-manual",
};
/*───────────────────────────*/

export default function HeroRegisterManual() {
  /* form fields */
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Admin");
  const [terms, setTerms] = useState(false);

  /* ui state */
  const [busy, setBusy] = useState(false);
  const [error, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [landing, setLanding] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("terms");
  const [emailValidationModalOpen, setEmailValidationModalOpen] =
    useState(false);

  /* steps */
  const [codeStep, setCodeStep] = useState(false);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [needVerify, setNeedVerify] = useState(false);
  const [curUser, setCurUser] = useState(null);
  const [codeExpiryTime, setCodeExpiryTime] = useState(null);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  /* registration state persistence */
  const [registrationState, setRegistrationState] = useState(() => {
    const saved = localStorage.getItem("heroRegistrationState");
    return saved ? JSON.parse(saved) : null;
  });

  const fn = getFunctions();
  const nav = useNavigate();
  const disp = useDispatch();

  /* prevent duplicate shared-record writes */
  const sharedWritten = useRef(false);

  /*──────────────── helper – get success route based on role ─────────────*/
  const getSuccessRoute = (userRole) => {
    switch (userRole) {
      case "Facilitator":
        return "/facilitator-dashboard";
      case "Admin":
      case "Management":
      case "CFO":
      case "Exco":
        return "/hero-dashboard";
      default:
        return "/hero-dashboard"; // Default to hero dashboard for all other roles
    }
  };

  /*──────────────── helper – create shared user record ─────────────*/
  const writeSharedRecord = async (userEmail) => {
    if (sharedWritten.current) return; // idempotent guard
    console.log(
      "[HR] ▶ createSharedUserRecord for",
      userEmail,
      "with role: ",
      role
    );
    const sharedFn = httpsCallable(fn, "createSharedUserRecord");
    await sharedFn({ email: userEmail, userRole: role });
    sharedWritten.current = true;
    console.log("[HR] shared user record written with role:", role);
  };

  /*──────────────── auto-redirect if already verified ──────────────*/
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    reload(u).then(async () => {
      console.log(
        "[HR] existing auth user:",
        u.email,
        "verified:",
        u.emailVerified
      );
      if (u.emailVerified) {
        await writeSharedRecord(u.email);
        nav(getSuccessRoute(role));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*──────────────── restore registration state on mount ───────────*/
  useEffect(() => {
    if (registrationState) {
      console.log("[HR] Restoring registration state:", registrationState);

      // Check if registration was completed successfully
      if (registrationState.completed) {
        console.log("[HR] Registration was completed, clearing state");
        localStorage.removeItem("heroRegistrationState");
        setRegistrationState(null);
        return;
      }

      // Restore form data
      setEmail(registrationState.email || "");
      setName(registrationState.name || "");
      setSurname(registrationState.surname || "");
      setPassword(registrationState.password || "");
      setRole(registrationState.role || "Admin");
      setTerms(registrationState.terms || false);

      // Restore step state
      if (registrationState.step === "code") {
        setCodeStep(true);
        setLanding(false);
        setCodeExpiryTime(registrationState.codeExpiryTime || null);
        setCanResend(registrationState.canResend || false);
        setResendCountdown(registrationState.resendCountdown || 0);
      } else if (registrationState.step === "verify") {
        setNeedVerify(true);
        setLanding(false);
      }

      // Clear the saved state after restoring
      localStorage.removeItem("heroRegistrationState");
      setRegistrationState(null);
    }
  }, [registrationState]);

  /*──────────────── save registration state ───────────────────────*/
  const saveRegistrationState = (step) => {
    const state = {
      step,
      email,
      name,
      surname,
      password,
      role,
      terms,
      timestamp: Date.now(),
    };

    if (step === "code") {
      state.codeExpiryTime = codeExpiryTime;
      state.canResend = canResend;
      state.resendCountdown = resendCountdown;
    } else if (step === "completed") {
      state.completed = true;
    }

    localStorage.setItem("heroRegistrationState", JSON.stringify(state));
    console.log("[HR] Saved registration state:", state);
  };

  /*──────────────── clear registration state ───────────────────────*/
  const clearRegistrationState = () => {
    localStorage.removeItem("heroRegistrationState");
    disp(clearRegistration());
    console.log("[HR] Registration state cleared");
  };

  const getLiveFirebaseUser = async () => {
    if (typeof auth.authStateReady === "function") {
      await auth.authStateReady();
    }

    const liveUser = auth.currentUser;
    if (liveUser && typeof liveUser.getIdToken === "function") {
      return liveUser;
    }

    if (curUser && typeof curUser.getIdToken === "function") {
      return curUser;
    }

    throw new Error("Your authentication session has expired. Please sign in again.");
  };

  /*──────────────── handle start registration ──────────────────────*/
  const handleStartRegistration = () => {
    clearRegistrationState();
    // Reset all form state
    setEmail("");
    setName("");
    setSurname("");
    setPassword("");
    setRole("Admin");
    setTerms(false);
    setCodeStep(false);
    setNeedVerify(false);
    setCode(["", "", "", "", "", ""]);
    setCodeExpiryTime(null);
    setCanResend(false);
    setResendCountdown(0);
    setCurUser(null);
    setErr("");
    setMsg("");
    setLanding(false);
  };

  /*──────────────── reset form state ───────────────────────────────*/
  const resetFormState = () => {
    setEmail("");
    setName("");
    setSurname("");
    setPassword("");
    setRole("Admin");
    setTerms(false);
    setCodeStep(false);
    setNeedVerify(false);
    setCode(["", "", "", "", "", ""]);
    setCodeExpiryTime(null);
    setCanResend(false);
    setResendCountdown(0);
    setCurUser(null);
    setErr("");
    setMsg("");
    setLanding(true);
  };

  /*──────────────── countdown timer effect ─────────────────────────*/
  useEffect(() => {
    if (codeStep && codeExpiryTime) {
      const timer = setInterval(() => {
        const now = Date.now();
        const timeLeft = Math.max(0, codeExpiryTime - now);

        if (timeLeft === 0) {
          setCodeExpiryTime(null);
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [codeStep, codeExpiryTime]);

  /*──────────────── resend countdown effect ────────────────────────*/
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  /*──────────────── save state on page unload ──────────────────────*/
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (codeStep) {
        saveRegistrationState("code");
      } else if (needVerify) {
        saveRegistrationState("verify");
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (codeStep) {
          saveRegistrationState("code");
        } else if (needVerify) {
          saveRegistrationState("verify");
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    codeStep,
    needVerify,
    email,
    name,
    surname,
    password,
    role,
    terms,
    codeExpiryTime,
    canResend,
    resendCountdown,
    curUser,
  ]);

  /*──────────────── cleanup effect ─────────────────────────────────*/
  useEffect(() => {
    return () => {
      // Clear registration state when component unmounts if user is verified
      if (curUser && curUser.emailVerified) {
        clearRegistrationState();
      }
    };
  }, [curUser]);

  /*──────────────── STEP 1 – generate validation code ──────────────*/
  const handleGenerateCode = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    if (!terms) return setErr("You must agree to the terms.");
    if (!(email && password && name && surname && role))
      return setErr("All fields required.");
    if (password.length < 6) return setErr("Password min 6 chars.");

    setBusy(true);
    try {
      // Check if user already exists
      const checkUser = httpsCallable(fn, "checkIfUserExists");
      const { data: existsData } = await checkUser({ email });
      if (existsData && existsData.exists) {
        setErr(
          <span>
            An account with this email already exists. Please{" "}
            <Link to="/hero-login-manual">login</Link> instead.
          </span>
        );
        setBusy(false);
        return;
      }
      console.log("[HR] ▶ createHeroValidation");
      const createVal = httpsCallable(fn, "createHeroValidation");
      const { data: valData } = await createVal({
        email,
        name,
        surname,
        userRole: role,
      });

      const html = valData?.html ?? valData?.data?.html;
      if (!html) throw new Error("Validation HTML not returned from CF");

      // Log the email template data for debugging
      console.log("[HR] 📧 Email Template Data:", {
        email,
        name,
        surname,
        role,
        validationCode: "6-digit code generated",
        timestamp: new Date().toLocaleString(),
        html: html.substring(0, 200) + "...", // Show first 200 chars of HTML
      });

      console.log("[HR] ▶ sendEmail to managers");
      const sendMail = httpsCallable(fn, "sendEmail");
      await sendMail({
        to: "clinton@tpc.training;marnus@tpc.training;conrad@tpc.training",
        from: "Hero Validation <noreply@mail.andus.co.za>",
        subject: "NEW Hero-Dashboard applicant – action required",
        isHTML: true,
        html,
      });

      setCodeStep(true);
      setMsg("Managers have the validation code. Ask them and enter it below.");

      // Set expiry time (24 hours from now)
      setCodeExpiryTime(Date.now() + 24 * 60 * 60 * 1000);

      // Start resend countdown (60 seconds)
      setCanResend(false);
      setResendCountdown(60);
    } catch (err) {
      console.error("[HR] handleGenerateCode error:", err);
      setErr(err.message ?? "Could not create validation code.");
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── STEP 2 – submit 6-digit code ───────────────────*/
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const fullCode = code.join("");
      console.log("[HR] ▶ verifyHeroValidation with", fullCode);
      const verify = httpsCallable(fn, "verifyHeroValidation");
      const { data: res } = await verify({
        email,
        validationCode: fullCode.trim(),
      });

      if (!res.success) throw new Error("Incorrect validation code.");

      // Clear saved state since registration is complete
      localStorage.removeItem("heroRegistrationState");

      // Instead of redirecting to login, proceed to email validation step
      await completeRegistration();
    } catch (err) {
      console.error("[HR] code submit error:", err);
      setErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── STEP 3 – Firebase auth + Firestore stub ────────*/
  const completeRegistration = async () => {
    setBusy(true);
    try {
      console.log("[HR] ▶ createUserWithEmailAndPassword");
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      setCurUser(user);
      // Dispatch all user info to Redux (add company, collectionBrand, etc.)
      disp(
        setUser({
          uid: user.uid,
          email: user.email,
          name,
          company: CONFIG.company,
          collectionBrand: CONFIG.collectionBrand,
        })
      );

      // Assign custom claim in Hero-{company}-{collectionBrand} format
      const customClaim = `Hero-${CONFIG.company}-${CONFIG.collectionBrand}`;
      await assignUserRole(user.uid, customClaim);
      console.log("[HR] custom claim set:", customClaim, "for role:", role);

      const fsOp = httpsCallable(fn, "firestoreOperations");
      await fsOp({
        operation: "write",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: CONFIG.systemName,
        uniqueId: email,
        documentData: {
          email,
          name,
          surname,
          role,
          state: CONFIG.state,
          timestamp: serverTimestamp(),
        },
        auth: true,
      });
      console.log("[HR] Firestore stub written");

      // Send verification email in background (non-blocking)
      // Show success message immediately without waiting
      setNeedVerify(true);
      setCodeStep(false); // Hide the code step
      setMsg(
        "Verification email is being sent. Please check your inbox in a moment."
      );

      // Clear registration state from localStorage and Redux
      clearRegistrationState();

      // Send email in background - don't block the UI
      // Configure action code settings for better email delivery
      const actionCodeSettings = {
        url: window.location.origin + "/hero-login-manual",
        handleCodeInApp: false,
      };

      sendEmailVerification(user, actionCodeSettings)
        .then(() => {
          console.log("[HR] verification e-mail sent successfully");
          setMsg(
            "Verification link sent! Check your inbox (including spam folder)."
          );
        })
        .catch((emailErr) => {
          console.error("[HR] Email verification send failed:", emailErr);
          // Still show success but note email issue
          setMsg(
            "Registration complete! If you don't receive the verification email, click 'Resend' below."
          );
        });
    } catch (err) {
      console.error("[HR] completeRegistration error:", err);
      setErr(err.message ?? "Registration error.");
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── helper – user clicks "I've verified" ───────────*/
  const handleVerifyRefresh = async () => {
    try {
      const u = await getLiveFirebaseUser();
      await reload(u);
      if (u.emailVerified) {
        await writeSharedRecord(u.email);

        // Clear all registration state before navigation
        clearRegistrationState();

        nav(getSuccessRoute(role));
      } else {
        setErr("Not verified yet.");
      }
    } catch (err) {
      setErr(err.message);
    }
  };

  /*──────────────── helper – resend verification email ───────────*/
  const handleResendVerification = async () => {
    try {
      const u = await getLiveFirebaseUser();

      // Configure action code settings for better email delivery
      const actionCodeSettings = {
        url: window.location.origin + "/hero-login-manual",
        handleCodeInApp: false,
      };

      await sendEmailVerification(u, actionCodeSettings);
      setMsg(
        "Verification e-mail resent. Check your inbox (including spam folder)."
      );
    } catch (err) {
      setErr(err.message);
    }
  };

  const handleCloseVerification = async () => {
    setBusy(true);
    try {
      await signOut(auth);
    } catch (signOutError) {
      console.warn("[HR] Could not sign out pending registration:", signOutError);
    } finally {
      sharedWritten.current = false;
      clearRegistrationState();
      resetFormState();
      setBusy(false);
    }
  };

  /*──────────────── code input handlers ────────────────────────────*/
  const handleCodeChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newCode = [...code];
    newCode[index] = value.replace(/\D/g, ""); // Only allow digits
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      const prevInput = document.getElementById(`code-digit-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    setBusy(true);
    try {
      console.log("[HR] ▶ createHeroValidation (resend)");
      const createVal = httpsCallable(fn, "createHeroValidation");
      const { data: valData } = await createVal({
        email,
        name,
        surname,
        userRole: role,
      });

      const html = valData?.html ?? valData?.data?.html;
      if (!html) throw new Error("Validation HTML not returned from CF");

      console.log("[HR] ▶ sendEmail to managers (resend)");
      const sendMail = httpsCallable(fn, "sendEmail");
      await sendMail({
        to: "clinton@tpc.training",
        from: "Hero Validation <noreply@mail.andus.co.za>",
        subject: "NEW Hero-Dashboard applicant – action required",
        isHTML: true,
        html,
      });

      setMsg("New validation code sent to managers.");
      setCodeExpiryTime(Date.now() + 24 * 60 * 60 * 1000);
      setCanResend(false);
      setResendCountdown(60);
    } catch (err) {
      console.error("[HR] resend code error:", err);
      setErr(err.message ?? "Could not resend validation code.");
    } finally {
      setBusy(false);
    }
  };

  const handleBackToRegister = () => {
    // Clear registration state when going back
    clearRegistrationState();
    resetFormState();
  };

  /*──────────────── modal handlers ─────────────────────────────────*/
  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  /*────────── render ──────────*/
  if (busy) return <LoadingCircleSmall label="Processing…" />;

  return (
    <div className="hr-container">
      {/* banner */}
      <div className="hr-left">
        <div className="hr-logo-cont">
          <img src={tpcAndUSLogo} alt="TPC" className="hr-logo" />
        </div>
        <div className="hr-welcome-cont">
          <div className="hr-welcome-text">Welcome to TPC</div>
        </div>
      </div>

      {/* right pane */}
      <div className="hr-right">
        {landing ? (
          <Landing
            onReg={handleStartRegistration}
            onLogin={() => nav(CONFIG.loginRoute)}
          />
        ) : codeStep ? (
          /* STEP 2 – code entry */
          <form className="hr-code-form" onSubmit={handleCodeSubmit}>
            <div className="hr-code-header">
              <button
                type="button"
                className="hr-back-button"
                onClick={handleBackToRegister}
                title="Back to registration"
              >
                ←
              </button>
              <h2 className="hr-code-title">Enter Validation Code</h2>
              <div style={{ width: "40px" }}></div> {/* Spacer for centering */}
            </div>

            {error && <p className="hr-err">{error}</p>}
            {msg && <p className="hr-success">{msg}</p>}

            {codeExpiryTime && (
              <div
                className={`hr-countdown ${
                  Date.now() > codeExpiryTime - 5 * 60 * 1000 ? "expiring" : ""
                }`}
              >
                Code expires in:{" "}
                {Math.max(
                  0,
                  Math.floor((codeExpiryTime - Date.now()) / 1000 / 60)
                )}
                m{" "}
                {Math.max(
                  0,
                  Math.floor(((codeExpiryTime - Date.now()) / 1000) % 60)
                )}
                s
              </div>
            )}

            <p className="hr-code-instructions">
              Please enter the <strong>6-digit validation code</strong> provided
              by the managers.
            </p>

            <div className="hr-code-input-container">
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-digit-${index}`}
                  type="text"
                  maxLength="1"
                  className={`hr-code-digit ${digit ? "filled" : ""} ${
                    error ? "error" : ""
                  }`}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  required
                />
              ))}
            </div>

            <button
              className="hr-button"
              type="submit"
              disabled={code.join("").length !== 6}
            >
              Continue
            </button>

            <div className="hr-resend-section">
              <p className="hr-resend-text">Didn't receive the code?</p>
              <button
                type="button"
                className="hr-resend-button"
                onClick={handleResendCode}
                disabled={!canResend || busy}
              >
                {canResend ? "Resend Code" : `Resend in ${resendCountdown}s`}
              </button>
            </div>
          </form>
        ) : needVerify ? (
          /* STEP 3 – e-mail verification */
          <VerifyStep
            err={error}
            msg={msg}
            onRefresh={handleVerifyRefresh}
            onResend={handleResendVerification}
            onClose={handleCloseVerification}
          />
        ) : (
          /* STEP 1 – initial form */
          <RegForm
            email={email}
            setEmail={setEmail}
            name={name}
            setName={setName}
            surname={surname}
            setSurname={setSurname}
            pw={password}
            setPw={setPassword}
            role={role}
            setRole={setRole}
            terms={terms}
            setTerms={setTerms}
            err={error}
            msg={msg}
            onSubmit={handleGenerateCode}
            openModal={openModal}
          />
        )}
      </div>

      {/* Terms and Privacy Modal */}
      <TermsModal isOpen={modalOpen} onClose={closeModal} type={modalType} />
    </div>
  );
}

/*────────── stateless helpers ──────────*/
const Landing = ({ onReg, onLogin }) => (
  <div className="hr-landing">
    <h2 className="hr-title">Join the Hero Dashboard</h2>
    <div className="hr-buttons">
      <button className="hr-button hr-button-primary" onClick={onReg}>
        Register
      </button>
      <button className="hr-button hr-button-secondary" onClick={onLogin}>
        Login
      </button>
    </div>
  </div>
);

const VerifyStep = ({ err, msg, onRefresh, onResend, onClose }) => (
  <div className="hr-verify">
    <button
      type="button"
      className="hr-verify-close"
      onClick={onClose}
      aria-label="Close verification and register another user"
      title="Register another user"
    >
      &times;
    </button>
    <h2 className="hr-title">Verify Your Email</h2>
    {err && <p className="hr-err">{err}</p>}
    {msg && <p className="hr-success">{msg}</p>}
    <p className="hr-terms">
      We've sent a verification link. Click below after verifying.
    </p>
    <button onClick={onRefresh} className="hr-refresh">
      I've Verified My Email
    </button>
    <p className="hr-terms">
      Didn't receive it?{" "}
      <button onClick={onResend} className="hr-resend-button">
        Resend
      </button>
    </p>
  </div>
);

const RegForm = ({
  email,
  setEmail,
  name,
  setName,
  surname,
  setSurname,
  pw,
  setPw,
  role,
  setRole,
  terms,
  setTerms,
  err,
  msg,
  onSubmit,
  openModal,
}) => {
  const roleOptions = [
    { value: "Admin", label: "Admin" },
    { value: "Management", label: "Management" },
    { value: "CFO", label: "CFO" },
    { value: "Exco", label: "Exco" },
    { value: "Facilitator", label: "Facilitator" },
  ];

  return (
    <form className="hr-form" onSubmit={onSubmit}>
      <h2 className="hr-title">Register for Hero Dashboard</h2>
      {err && <p className="hr-err">{err}</p>}
      {msg && <p className="hr-success">{msg}</p>}
      <input
        type="text"
        placeholder="Full Name"
        className="hr-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <input
        type="text"
        placeholder="Surname"
        className="hr-input"
        value={surname}
        onChange={(e) => setSurname(e.target.value)}
        required
      />
      <CustomDropdown
        value={role}
        onChange={setRole}
        options={roleOptions}
        placeholder="Select Role"
        className="hr-input"
      />
      <input
        type="email"
        placeholder="Email"
        className="hr-input"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <input
        type="password"
        placeholder="Password"
        className="hr-input"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        required
      />
      <div className="hr-check-cont">
        <input
          type="checkbox"
          className="hr-check"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          required
        />
        <label className="hr-check-label">
          I agree to&nbsp;
          <button
            type="button"
            className="hr-link-button"
            onClick={() => openModal("terms")}
          >
            Terms
          </button>
          &nbsp;and&nbsp;
          <button
            type="button"
            className="hr-link-button"
            onClick={() => openModal("privacy")}
          >
            Privacy
          </button>
          .
        </label>
      </div>
      <button type="submit" className="hr-button">
        Generate Code
      </button>
    </form>
  );
};
